import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage, useTheme, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sun, Moon, Globe, ArrowLeft, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const ForgotPassword = () => {
  const { t, language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error(language === "it" ? "Inserisci la tua email" : "Enter your email");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
      toast.success(
        language === "it" 
          ? "Se l'email è registrata, riceverai un link per reimpostare la password."
          : "If the email is registered, you will receive a password reset link."
      );
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center pt-16 px-4">
        <div className="w-full max-w-md">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna al login" : "Back to login"}
          </Link>

          <div className="p-8 rounded-2xl border bg-card shadow-card">
            {!submitted ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-accent" />
                  </div>
                  <h1 className="text-2xl font-serif font-medium">
                    {language === "it" ? "Password Dimenticata?" : "Forgot Password?"}
                  </h1>
                  <p className="mt-2 text-muted-foreground">
                    {language === "it" 
                      ? "Inserisci la tua email e ti invieremo un link per reimpostare la password."
                      : "Enter your email and we'll send you a link to reset your password."
                    }
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="esempio@email.com"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-full"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading 
                      ? (language === "it" ? "Invio in corso..." : "Sending...") 
                      : (language === "it" ? "Invia Link di Reset" : "Send Reset Link")
                    }
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold mb-2">
                  {language === "it" ? "Email Inviata!" : "Email Sent!"}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {language === "it" 
                    ? "Se l'email è registrata nel sistema, riceverai un link per reimpostare la password. Controlla anche la cartella spam."
                    : "If the email is registered, you will receive a password reset link. Please also check your spam folder."
                  }
                </p>
                <Link to="/login">
                  <Button variant="outline" className="rounded-full">
                    {language === "it" ? "Torna al Login" : "Back to Login"}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {language === "it" ? "Ricordi la password?" : "Remember your password?"}{" "}
            <Link to="/login" className="text-accent hover:underline">
              {t("login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
